/**
 * src/lib/contactSubmission.ts
 * ------------------------------------------------------------------
 * A CLASS TEACHER PROPOSES AN ADDRESS; AN ADMIN DECIDES.
 *
 * 156 families have never given LKC an email address, so they receive no
 * reports, no invoices and no notices. Nothing in the portal will produce
 * one. The person who can ask is the class teacher, who sees the child every
 * day — but a class teacher is not an administrator, and where the school
 * writes to a family is not a teacher's decision to make alone.
 *
 * So nothing a teacher types takes effect on its own. It is stored PENDING
 * and an admin approves it, exactly as the parent self-service flow already
 * works (`reportGuardianUpdates`, 301 approvals and counting). Teachers never
 * touch `reportStudents`; the approving callable does, with the Admin SDK.
 * That also keeps SECURITY-BACKLOG item 1 open rather than making it worse —
 * it wants teaching roles OFF student records, and this adds no new path on.
 *
 * WHAT THIS MODULE IS. The rules, as pure functions, so the screen and the
 * Cloud Function agree about what may be submitted and what may be approved,
 * and so both can be tested without Firestore.
 *
 * THE RULE THAT MATTERS MOST: a submission may only FILL A GAP. If the school
 * already holds a working address for a pupil, it is not a gap, and no
 * teacher may propose replacing it. Collecting a missing address and
 * redirecting a family's mail are different acts, and only one of them is
 * being delegated here.
 *
 * MIRRORED at functions/lib/contactSubmission.js. Change one, change both.
 * ------------------------------------------------------------------ */
import { isRealAddress } from './parentRecipient';

export type SubmissionStatus = 'pending' | 'approved' | 'rejected' | 'superseded';

export interface ContactSubmission {
  id: string;
  studentId: string;
  studentName: string;
  classId: string;
  className: string;
  /** Which guardian this is for, when the pupil has one on file. */
  guardianId?: string;
  /** The guardian's name — supplied when the pupil has no guardian record at
   *  all, which is true of 51 of the 156. */
  guardianName?: string;
  email: string;
  phone?: string;
  /** What the school held when this was proposed, for the approver to see. */
  previousEmail?: string;
  status: SubmissionStatus;
  submittedBy: string;
  submittedByName?: string;
  submittedAt: string;
  reviewedBy?: string;
  reviewedByName?: string;
  reviewedAt?: string;
  rejectionReason?: string;
}

export interface SubmissionDraft {
  studentId: string;
  email: string;
  guardianId?: string;
  guardianName?: string;
  phone?: string;
}

/**
 * Why this draft cannot be submitted, or null if it can.
 *
 * Returned as a sentence for a person to read, because it is shown to the
 * teacher who typed it and "invalid input" tells them nothing about what to
 * do next.
 */
export function submissionProblem(
  draft: SubmissionDraft,
  opts: {
    /** The address the school currently holds and can actually use. */
    workingEmail?: string | null;
    /** Addresses already pending for this pupil, to catch a double send. */
    pendingEmails?: readonly string[];
  } = {},
): string | null {
  const email = String(draft.email ?? '').trim();
  if (!email) return 'Enter the parent’s email address.';
  if (!isRealAddress(email)) {
    /* The manufactured `parent.lkc.ac.bw` addresses look like addresses, and
       a teacher copying one off the old list is the likeliest mistake here. */
    return 'That is not an address the school can send to. '
      + 'Addresses ending @parent.lkc.ac.bw were created by an old import and do not work.';
  }
  if (!draft.studentId) return 'No pupil was selected.';

  /* FILLING A GAP, NOT REDIRECTING A FAMILY. */
  if (opts.workingEmail && isRealAddress(opts.workingEmail)) {
    return `The school already has a working address for this pupil (${opts.workingEmail}). `
      + 'Ask the office to change it.';
  }

  const already = (opts.pendingEmails ?? []).some(
    (e) => String(e).trim().toLowerCase() === email.toLowerCase(),
  );
  if (already) return 'That address has already been submitted and is waiting for approval.';

  return null;
}

/** Why this submission cannot be approved, or null if it can. */
export function approvalProblem(
  submission: Pick<ContactSubmission, 'status' | 'email'>,
  opts: { workingEmail?: string | null } = {},
): string | null {
  if (submission.status !== 'pending') {
    return `This submission has already been ${submission.status}.`;
  }
  if (!isRealAddress(submission.email)) {
    return 'The submitted address cannot receive mail.';
  }
  /* Between submission and approval somebody may have supplied an address by
     another route — the parent registering for the portal, say. Approving on
     top of that would overwrite a working address with an unverified one. */
  if (opts.workingEmail && isRealAddress(opts.workingEmail)
      && opts.workingEmail.trim().toLowerCase() !== submission.email.trim().toLowerCase()) {
    return `The school now has a working address for this pupil (${opts.workingEmail}), `
      + 'added since this was submitted. Reject this one, or change it in the office.';
  }
  return null;
}

/** A tidy address: what gets stored, so two spellings never both land. */
export function normaliseEmail(email: string): string {
  return String(email ?? '').trim().toLowerCase();
}

/**
 * How many pupils in each class are still without an address.
 *
 * Counted AFTER pending submissions are taken out, because a teacher who has
 * done their part should see the number fall — otherwise the daily reminder
 * keeps naming pupils they have already dealt with, and they stop reading it.
 */
export function outstandingCount(
  pupils: readonly { studentId: string }[],
  pending: readonly { studentId: string; status: SubmissionStatus }[],
): number {
  const waiting = new Set(
    pending.filter((p) => p.status === 'pending').map((p) => p.studentId),
  );
  return pupils.filter((p) => !waiting.has(p.studentId)).length;
}
